import React from 'react'
import ReactDOM from 'react-dom/client'
import App from '@/App.jsx'
import '@/index.css'
// The plugin's auto-injected registerSW.js (a bare `navigator.serviceWorker
// .register('/sw.js')` with no update handling) only ever installs a new
// service worker in the background -- it never tells an already-open tab
// to actually start using it, so users could sit on a stale build
// indefinitely even after a successful deploy + a manual reload.
//
// registerType is 'prompt' (see vite.config.js), NOT 'autoUpdate' --
// autoUpdate reloads unconditionally on every detected service-worker
// change with no loop guard, so if the precache diff is ever flaky
// (a byte that legitimately differs between two fetches of the "same"
// build, e.g. anything non-deterministic in the build output) it
// reloads forever, which looks exactly like the page being "crashed"/
// stuck. Handling onNeedRefresh ourselves lets us cap it to at most one
// automatic reload per browser session -- still self-heals a genuinely
// stale tab, but can never loop.
import { registerSW } from 'virtual:pwa-register'
import { Capacitor } from '@capacitor/core'

const RELOAD_GUARD_KEY = 'prohaul.sw_last_reload'
// A true one-time-per-session cap (see git history) turned out to have its
// own failure mode: a staff member mid-onboarding (fill profile -> sign W-4
// -> land in the app) can sit on one tab through several deploys during a
// busy dev day. The first stale-bundle reload used up the session's only
// allowed reload, so every deploy after that left her running further and
// further behind -- reported as the app going fully unusable ("stuck on
// /admin, only a full tab close/reopen fixes it") since a normal reload is
// still served by the SW's own cached (stale) app shell. A cooldown instead
// of a hard cap lets the tab keep self-healing every few minutes across a
// long session, while the window is still wide enough that a burst of
// several deploys in a row (normal during active development) doesn't
// force-reload out from under someone on every single one.
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000

// All of the reload-guard machinery below exists to fight a web-only
// failure mode: a tab left open across a deploy silently running a stale
// cached bundle. Inside the Capacitor Android shell there's no "deploy" to
// go stale against -- the whole app updates atomically via the Play Store
// -- so registering a service worker there would only add unnecessary
// caching complexity (and risk reintroducing the exact staleness bug this
// code fights, for no benefit) instead of just always loading the bundle
// packaged into the app.
const updateSW = Capacitor.isNativePlatform() ? null : registerSW({
  immediate: true,
  onRegisteredSW(swUrl, registration) {
    if (!registration) return;
    // This is an SPA -- users rarely trigger a full page navigation,
    // which is the only thing that makes the browser natively re-check
    // sw.js for changes. A tab left open across a deploy would otherwise
    // never notice a new service worker exists, so onNeedRefresh below
    // never fires and the tab keeps running whatever bundle it started
    // with indefinitely -- even across several deploys in a row (lived
    // this exact scenario during a single rapid-fire session: multiple
    // successful deploys went out, but a tab kept testing each one
    // immediately and kept landing one deploy behind).
    const checkForUpdate = () => registration.update().catch(() => {});
    setInterval(checkForUpdate, 5 * 60 * 1000);
    // The much more common real-world trigger than the timer: the user
    // tabs away (to read a message, check something else) and back.
    // Checking on that beats waiting up to 5 minutes.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    });
  },
  onNeedRefresh() {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    updateSW(true);
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <App />
)
